import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedNumber, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '__generated__/globalTypes';
import mlbUrl from 'assets/logos/mlb/mlb.svg';
import nbaUrl from 'assets/logos/nba/nba.svg';
import { Horizontal } from 'atoms/layout/flex';
import { useCurrentUserContext } from 'contexts/currentUser';
import useQuery from 'hooks/graphql/useQuery';
import { sportsLabelsMessages } from 'lib/glossary';
import { FootballLogo } from 'routing/MultiSportAppBar/Sport/Football';

import SettingsSection from '../SettingsSection';
import {
  ActivityReportQuery,
  ActivityReportQueryVariables,
} from './__generated__/index.graphql';

const messages = defineMessages({
  title: {
    id: 'Settings.activityReports.title',
    defaultMessage: 'Activity Reports',
  },
  description: {
    id: 'Settings.activityReports.description',
    defaultMessage:
      'See how many tournaments you have entered in the last 30 days',
  },
});

const ACTIVITY_REPORT_QUERY = gql`
  query ActivityReportQuery {
    currentUser {
      slug
      footballLast30DaysLineupsCount
      nbaLast30DaysLineupsCount
      baseballLast30DaysLineupsCount
    }
  }
` as TypedDocumentNode<ActivityReportQuery, ActivityReportQueryVariables>;

const Logo = styled.img`
  max-width: 30px;
  height: 30px;
  object-fit: contain;
`;

const logos = {
  [Sport.FOOTBALL]: <FootballLogo height={40} aria-label={Sport.FOOTBALL} />,
  [Sport.NBA]: <Logo src={nbaUrl} aria-label={Sport.NBA} />,
  [Sport.BASEBALL]: <Logo src={mlbUrl} aria-label={Sport.BASEBALL} />,
};

const ActivityReports = () => {
  const { data, loading } = useQuery(ACTIVITY_REPORT_QUERY);
  const { currentUser } = useCurrentUserContext();
  const { formatMessage } = useIntl();
  if (!currentUser) {
    return null;
  }

  const countsBySport = {
    [Sport.FOOTBALL]: data?.currentUser?.footballLast30DaysLineupsCount,
    [Sport.NBA]: data?.currentUser?.nbaLast30DaysLineupsCount,
    [Sport.BASEBALL]: data?.currentUser?.baseballLast30DaysLineupsCount,
  };

  return (
    <SettingsSection {...messages}>
      <Horizontal gap={4}>
        {[Sport.FOOTBALL, Sport.NBA, Sport.BASEBALL].map(sport => (
          <Horizontal
            key={sport}
            title={formatMessage(sportsLabelsMessages[sport])}
          >
            {logos[sport]}
            {loading ? (
              '...'
            ) : (
              <FormattedNumber value={countsBySport[sport] || 0} />
            )}
          </Horizontal>
        ))}
      </Horizontal>
    </SettingsSection>
  );
};

export default ActivityReports;
